import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, User } from "lucide-react";

const AUSTRALIAN_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "De Facto", "Divorced", "Widowed", "Separated"];
const EMPLOYMENT_TYPES = ["Full Time", "Part Time", "Casual/Temp", "Contract", "Self Employed"];
const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr"];

const emptyGuarantor = {
  title: "",
  given_names: "",
  surname: "",
  date_of_birth: "",
  drivers_licence: "",
  dependents: "",
  marital_status: "",
  phone_home: "",
  mobile: "",
  email: "",
  residential_address: {
    unit_no: "",
    street_no: "",
    street_name: "",
    suburb: "",
    state: "",
    postcode: "",
  },
  occupation: "",
  employer_name: "",
  employment_type: "",
  annual_income: 0,
};

// APP-FORM-FIX (2026-07-25) — legacy/partial guarantor records (e.g. saved
// before residential_address existed on every row, or created via
// admin quick-create) can arrive from the backend missing residential_address
// entirely. Every read below assumes that nested object exists — normalizing
// on the way in prevents a hard crash ("Cannot read properties of undefined
// (reading 'unit_no')") when such a record is opened.
const normalizeGuarantor = (g) => ({
  ...emptyGuarantor,
  ...(g || {}),
  residential_address: {
    ...emptyGuarantor.residential_address,
    ...((g && g.residential_address) || {}),
  },
});

export const StepGuarantors = ({ formData, updateFormData, prefillData }) => {
  const [guarantors, setGuarantors] = useState(
    formData.guarantors.length > 0
      ? formData.guarantors.map(normalizeGuarantor)
      : [{ ...emptyGuarantor }]
  );
  const [activeTab, setActiveTab] = useState("0");
  // APP-FORM-FIX (2026-07-25) — same root cause as StepCompanyBorrower's
  // prefill Select: uncontrolled Radix Select tracks its own internal
  // value, so re-picking the SAME previous guarantor a second time never
  // re-fires onValueChange. Bumping a per-row reset key forces a remount
  // after every pick so the next selection always fires, even a repeat.
  const [prefillResetKeys, setPrefillResetKeys] = useState({});

  useEffect(() => {
    updateFormData("guarantors", guarantors);
  }, [guarantors, updateFormData]);

  const handleGuarantorChange = (index, field, value) => {
    const newGuarantors = [...guarantors];
    newGuarantors[index] = { ...newGuarantors[index], [field]: value };
    setGuarantors(newGuarantors);
  };

  const handleAddressChange = (index, field, value) => {
    const newGuarantors = [...guarantors];
    newGuarantors[index] = {
      ...newGuarantors[index],
      residential_address: {
        ...newGuarantors[index].residential_address,
        [field]: value,
      },
    };
    setGuarantors(newGuarantors);
  };

  const addGuarantor = () => {
    if (guarantors.length < 4) {
      const newGuarantors = [...guarantors, { ...emptyGuarantor }];
      setGuarantors(newGuarantors);
      setActiveTab(String(newGuarantors.length - 1));
    }
  };

  const prefillGuarantor = (index, gName) => {
    setPrefillResetKeys((prev) => ({ ...prev, [index]: (prev[index] || 0) + 1 }));
    if (!prefillData?.guarantors) return;
    const match = prefillData.guarantors.find(g => g.name === gName);
    if (match && match.data) {
      const newGuarantors = [...guarantors];
      newGuarantors[index] = normalizeGuarantor(match.data);
      setGuarantors(newGuarantors);
      // APP-FORM-FIX (2026-07-25) — see LoanApplicationForm.jsx autosave
      // comment: without this, a silent local-state prefill + no backend
      // persistence until Continue/Save Progress made "prefill" look
      // broken if the client reloaded (or their session dropped) first.
      toast.success(`Prefilled from "${gName}"`);
    }
  };

  const removeGuarantor = (index) => {
    if (guarantors.length > 1) {
      const newGuarantors = guarantors.filter((_, i) => i !== index);
      setGuarantors(newGuarantors);
      setActiveTab("0");
      toast.success("Guarantor removed — this saves automatically");
    }
  };

  const renderGuarantorForm = (guarantor, index) => (
    <div className="space-y-8" data-testid={`guarantor-form-${index}`}>
      {/* Prefill from previous guarantors */}
      {prefillData?.guarantors?.length > 0 && (
        <div className="p-3 bg-[#021d49]/5 border border-[#021d49]/10 rounded">
          <Label className="text-xs font-medium text-[#021d49] mb-1 block">Quick Fill from Previous Guarantors</Label>
          <Select key={`prefill-guarantor-${index}-${prefillResetKeys[index] || 0}`} onValueChange={(v) => prefillGuarantor(index, v)}>
            <SelectTrigger className="form-input h-9 text-sm" data-testid={`prefill-guarantor-${index}`}>
              <SelectValue placeholder="Select a previous guarantor..." />
            </SelectTrigger>
            <SelectContent>
              {prefillData.guarantors.map((g) => (
                <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Personal Details */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Personal Details {`• Guarantor ${index + 1}`}</h4>
        
        <div className="form-grid form-grid-3">
          <div>
            <Label className="form-label">Title</Label>
            <Select
              value={guarantor.title}
              onValueChange={(v) => handleGuarantorChange(index, "title", v)}
            >
              <SelectTrigger className="form-input" data-testid={`guarantor-title-${index}`}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {TITLE_OPTIONS.map((title) => (
                  <SelectItem key={title} value={title}>{title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="form-label form-label-required">Given Names</Label>
            <Input
              value={guarantor.given_names}
              onChange={(e) => handleGuarantorChange(index, "given_names", e.target.value)}
              placeholder="First and middle names"
              className="form-input"
              data-testid={`guarantor-given-names-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">Surname</Label>
            <Input
              value={guarantor.surname}
              onChange={(e) => handleGuarantorChange(index, "surname", e.target.value)}
              placeholder="Last name"
              className="form-input"
              data-testid={`guarantor-surname-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">Date of Birth</Label>
            <Input
              type="date"
              value={guarantor.date_of_birth}
              onChange={(e) => handleGuarantorChange(index, "date_of_birth", e.target.value)}
              className="form-input"
              data-testid={`guarantor-dob-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label">Driver's Licence No.</Label>
            <Input
              value={guarantor.drivers_licence}
              onChange={(e) => handleGuarantorChange(index, "drivers_licence", e.target.value)}
              placeholder="Licence number"
              className="form-input"
              data-testid={`guarantor-licence-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label">Number of Dependents & Age(s)</Label>
            <Input
              value={guarantor.dependents}
              onChange={(e) => handleGuarantorChange(index, "dependents", e.target.value)}
              placeholder="e.g., 2 (ages 5, 8)"
              className="form-input"
              data-testid={`guarantor-dependents-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label">Marital Status</Label>
            <Select
              value={guarantor.marital_status}
              onValueChange={(v) => handleGuarantorChange(index, "marital_status", v)}
            >
              <SelectTrigger className="form-input" data-testid={`guarantor-marital-${index}`}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {MARITAL_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Contact Details */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Contact Details</h4>
        
        <div className="form-grid form-grid-3">
          <div>
            <Label className="form-label">Home Phone</Label>
            <Input
              value={guarantor.phone_home}
              onChange={(e) => handleGuarantorChange(index, "phone_home", e.target.value)}
              placeholder="(XX) XXXX XXXX"
              className="form-input"
              data-testid={`guarantor-phone-home-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">Mobile</Label>
            <Input
              value={guarantor.mobile}
              onChange={(e) => handleGuarantorChange(index, "mobile", e.target.value)}
              placeholder="04XX XXX XXX"
              className="form-input"
              data-testid={`guarantor-mobile-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">Email Address</Label>
            <Input
              type="email"
              value={guarantor.email}
              onChange={(e) => handleGuarantorChange(index, "email", e.target.value)}
              placeholder="email@example.com"
              className="form-input"
              data-testid={`guarantor-email-${index}`}
            />
          </div>
        </div>
      </section>

      {/* Residential Address */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Residential Address</h4>
        
        <div className="form-grid form-grid-3">
          <div>
            <Label className="form-label">Unit No.</Label>
            <Input
              value={guarantor.residential_address.unit_no}
              onChange={(e) => handleAddressChange(index, "unit_no", e.target.value)}
              placeholder="Unit"
              className="form-input"
              data-testid={`guarantor-address-unit-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">Street No.</Label>
            <Input
              value={guarantor.residential_address.street_no}
              onChange={(e) => handleAddressChange(index, "street_no", e.target.value)}
              placeholder="Number"
              className="form-input"
              data-testid={`guarantor-address-street-no-${index}`}
            />
          </div>
          <div>
            <Label className="form-label form-label-required">Street Name</Label>
            <Input
              value={guarantor.residential_address.street_name}
              onChange={(e) => handleAddressChange(index, "street_name", e.target.value)}
              placeholder="Street name"
              className="form-input"
              data-testid={`guarantor-address-street-name-${index}`}
            />
          </div>
          <div>
            <Label className="form-label form-label-required">Suburb</Label>
            <Input
              value={guarantor.residential_address.suburb}
              onChange={(e) => handleAddressChange(index, "suburb", e.target.value)}
              placeholder="Suburb"
              className="form-input"
              data-testid={`guarantor-address-suburb-${index}`}
            />
          </div>
          <div>
            <Label className="form-label form-label-required">State</Label>
            <Select
              value={guarantor.residential_address.state}
              onValueChange={(v) => handleAddressChange(index, "state", v)}
            >
              <SelectTrigger className="form-input" data-testid={`guarantor-address-state-${index}`}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {AUSTRALIAN_STATES.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label form-label-required">Postcode</Label>
            <Input
              value={guarantor.residential_address.postcode}
              onChange={(e) => handleAddressChange(index, "postcode", e.target.value)}
              placeholder="XXXX"
              maxLength={4}
              className="form-input"
              data-testid={`guarantor-address-postcode-${index}`}
            />
          </div>
        </div>
      </section>

      {/* Employment Details */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Employment Details</h4>
        
        <div className="form-grid form-grid-2">
          <div>
            <Label className="form-label form-label-required">Occupation</Label>
            <Input
              value={guarantor.occupation}
              onChange={(e) => handleGuarantorChange(index, "occupation", e.target.value)}
              placeholder="Your occupation"
              className="form-input"
              data-testid={`guarantor-occupation-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label">Employer Name / Trading Name</Label>
            <Input
              value={guarantor.employer_name}
              onChange={(e) => handleGuarantorChange(index, "employer_name", e.target.value)}
              placeholder="If self-employed, your trading name"
              className="form-input"
              data-testid={`guarantor-employer-${index}`}
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">Employment Type</Label>
            <Select
              value={guarantor.employment_type}
              onValueChange={(v) => handleGuarantorChange(index, "employment_type", v)}
            >
              <SelectTrigger className="form-input" data-testid={`guarantor-employment-type-${index}`}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="form-label form-label-required">Annual Income (Before Tax)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={guarantor.annual_income || ""}
                onChange={(e) => handleGuarantorChange(index, "annual_income", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid={`guarantor-income-${index}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Remove Guarantor Button */}
      {guarantors.length > 1 && (
        <div className="flex justify-end">
          <button
            onClick={() => removeGuarantor(index)}
            className="text-red-500 hover:text-red-700 flex items-center gap-2 px-4 py-2 rounded border border-red-200 hover:bg-red-50 transition-colors"
            data-testid={`remove-guarantor-${index}`}
          >
            <Trash2 className="w-4 h-4" />
            Remove Guarantor {index + 1}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-sm text-gray-500">
            Add up to 4 guarantors for this application. At least one guarantor is required.
          </p>
        </div>
        {guarantors.length < 4 && (
          <button
            onClick={addGuarantor}
            className="btn-secondary px-4 py-2 rounded flex items-center gap-2"
            data-testid="add-guarantor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Guarantor
          </button>
        )}
      </div>

      {guarantors.length === 1 ? (
        renderGuarantorForm(guarantors[0], 0)
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start mb-6 bg-gray-100 p-1 rounded">
            {guarantors.map((g, index) => (
              <TabsTrigger
                key={index}
                value={String(index)}
                className="flex items-center gap-2 data-[state=active]:bg-[#021d49] data-[state=active]:text-white rounded px-4 py-2"
                data-testid={`guarantor-tab-${index}`}
              >
                <User className="w-4 h-4" />
                Guarantor {index + 1}
                {g.given_names && g.surname && (
                  <span className="text-xs opacity-75">
                    ({g.given_names} {g.surname})
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {guarantors.map((guarantor, index) => (
            <TabsContent key={index} value={String(index)}>
              {renderGuarantorForm(guarantor, index)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};
